import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { StaffMember } from '@popcart/core/v1';
import { api } from '../../api';
import { ConfirmDialog, Loading, Segmented, StatusPill, useNow, useTitle } from '../../ui/bits';
import { Scene, SceneError } from '../../catalog/art';
import { Icon } from '../../ui/Icon';
import { RowMenu } from '../../ui/viz';
import { useToast } from '../../ui/toast';
import { useAuth } from '../../auth/AuthProvider';
import { initials } from '../../lib/format.js';
import { useShop } from '../ShopLayout';
import {
  MAX_EMAIL,
  OWNER_ONLY,
  ROLE_LABEL,
  ROLE_OPTIONS,
  addedText,
  canManageTeam,
  displayName,
  emailProblem,
  lastSeen,
  removeBlock,
  sortStaff,
  staffActions,
  staffProblem,
  type StaffRole,
} from './lib/staff.js';
import { staffKeys } from './keys';
import '../shop.css';
import './staff.css';

/** /shop/:shopId/staff — the team. Everyone sees it; only the owner adds or removes people. */
export function ShopStaffPage() {
  useTitle('Staff');
  const { shopId } = useShop();
  const { me } = useAuth();
  const owner = canManageTeam(me, shopId);
  const team = useQuery({ queryKey: staffKeys.team(shopId), queryFn: () => api.shopStaff(shopId), enabled: !!shopId });
  const list = useMemo(() => sortStaff(team.data ?? []), [team.data]);
  const [removing, setRemoving] = useState<StaffMember | null>(null);

  return (
    <>
      <header className="dv-head">
        <div className="stack s4">
          <h1>Staff</h1>
          <span className="dv-live" title="Everyone who can open this Partner Hub">
            {team.data ? `${list.length} ${list.length === 1 ? 'person' : 'people'}` : 'Your team'}
          </span>
        </div>
        {team.data && list.length ? (
          <div className="dv-actions st-faces" aria-hidden="true">
            {list.slice(0, 6).map((m) => (
              <span key={m.userId} className="avatar">
                {initials(m.name, m.email)}
              </span>
            ))}
          </div>
        ) : null}
      </header>

      <div className="st-grid">
        <section className="card dv-card dv-rise" aria-labelledby="st-team-h" aria-busy={team.isFetching || undefined}>
          <div className="dv-card-h">
            <h2 id="st-team-h">The team</h2>
            {team.data ? <span className="dv-chip">{list.length}</span> : null}
            {team.data ? <span className="dv-chip">{list.filter((m) => m.role === 'shop_owner').length} owner{list.filter((m) => m.role === 'shop_owner').length === 1 ? '' : 's'}</span> : null}
          </div>
          {team.isError && !team.data ? (
            <SceneError error={team.error} onRetry={() => void team.refetch()} compact />
          ) : !team.data ? (
            <Loading label="Loading the team" rows={2} />
          ) : list.length === 0 ? (
            <Scene scene="noResults" compact title="No one here yet">
              People you add sign in with their email and land on this shop.
            </Scene>
          ) : (
            <ul className="st-list" aria-label="Team members">
              {list.map((m) => (
                <li key={m.userId}>
                  <MemberRow m={m} team={list} owner={owner} onRemove={() => setRemoving(m)} />
                </li>
              ))}
            </ul>
          )}
        </section>

        {owner ? (
          <AddPerson shopId={shopId} team={list} />
        ) : (
          <section className="card dv-card dv-rise" style={{ ['--i' as string]: 1 }} aria-labelledby="st-add-h">
            <div className="dv-card-h">
              <h2 id="st-add-h">Add someone</h2>
            </div>
            <p className="ph-note st-locked" role="note">
              <span className="row">
                <Icon name="alert" size="sm" />
                <b>{OWNER_ONLY}</b>
              </span>
              <span className="dv-sub">Ask them to add the person for you.</span>
            </p>
          </section>
        )}
      </div>

      {owner ? <RemoveDialog shopId={shopId} member={removing} onClose={() => setRemoving(null)} /> : null}
    </>
  );
}

function MemberRow({ m, team, owner, onRemove }: { m: StaffMember; team: StaffMember[]; owner: boolean; onRemove(): void }) {
  const now = useNow(60_000);
  const block = removeBlock(m, team);
  const name = displayName(m);
  return (
    <div className="st-row">
      <span className="avatar" aria-hidden="true">
        {initials(m.name, m.email)}
      </span>
      <span className="st-who">
        <span className="row wrap">
          <b className="st-name">{name}</b>
          {m.you ? <span className="pill sticker st-you">You</span> : null}
        </span>
        {m.email && m.email !== name ? <span className="dv-sub st-email">{m.email}</span> : null}
        {owner && block ? <span className="dv-sub st-why">{block}</span> : null}
      </span>
      <span className="dv-sub st-seen" title={addedText(m.addedAt)}>
        {lastSeen(m.lastLoginAt, now)}
      </span>
      <StatusPill tone={m.role === 'shop_owner' ? 'success' : 'neutral'}>{ROLE_LABEL[m.role]}</StatusPill>
      {owner ? (
        block ? (
          <span className="st-block" title={block} aria-hidden="true" />
        ) : (
          <RowMenu label={`Actions for ${name}`} items={[{ label: `Remove ${name}`, icon: 'close', tone: 'danger', onSelect: onRemove }]} />
        )
      ) : (
        <span className="st-block" aria-hidden="true" />
      )}
    </div>
  );
}

function AddPerson({ shopId, team }: { shopId: string; team: StaffMember[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const actions = useMemo(() => staffActions(api, shopId), [shopId]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<StaffRole>('shop_staff');
  const [err, setErr] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () => actions.add(email, role),
    onSuccess: (m) => {
      qc.setQueryData<StaffMember[]>(staffKeys.team(shopId), (xs) => [...(xs ?? []).filter((x) => x.userId !== m.userId), m]);
      void qc.invalidateQueries({ queryKey: staffKeys.team(shopId) });
      toast(`${displayName(m)} added as ${ROLE_LABEL[m.role].toLowerCase()}. They sign in with that email.`);
      setEmail('');
      setRole('shop_staff');
      setErr(null);
    },
    onError: (e) => setErr(staffProblem(e as { status?: number; message?: string }, 'add')),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const p = emailProblem(email, team);
    setErr(p);
    if (!p) add.mutate();
  };
  const help = ROLE_OPTIONS.find((o) => o.value === role)?.help;

  return (
    <form className="card dv-card st-add dv-rise" style={{ ['--i' as string]: 1 }} aria-labelledby="st-add-h" onSubmit={onSubmit} noValidate>
      <div className="dv-card-h">
        <h2 id="st-add-h">Add someone</h2>
      </div>
      <div className="field">
        <label htmlFor="st-email">Their email</label>
        <input
          id="st-email"
          type="email"
          className="input"
          autoComplete="off"
          inputMode="email"
          maxLength={MAX_EMAIL}
          value={email}
          aria-invalid={err ? true : undefined}
          aria-describedby={err ? 'st-email-err' : 'st-email-help'}
          onChange={(e) => {
            setEmail(e.target.value);
            if (err) setErr(null);
          }}
        />
        {err ? (
          <span id="st-email-err" className="pill st-error err-pill" role="alert">
            {err}
          </span>
        ) : (
          <span id="st-email-help" className="help">
            They sign in with this email
          </span>
        )}
      </div>
      <div className="field">
        <span className="flabel" id="st-role-l">
          Role
        </span>
        <Segmented<StaffRole> label="Role" value={role} options={ROLE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))} onChange={setRole} />
        {help ? <span className="help">{help}</span> : null}
      </div>
      <div className="row">
        <button type="submit" className="btn btn-primary" disabled={add.isPending} aria-busy={add.isPending || undefined}>
          <Icon name="plus" />
          {add.isPending ? 'Adding…' : 'Add to the team'}
        </button>
      </div>
    </form>
  );
}

function RemoveDialog({ shopId, member, onClose }: { shopId: string; member: StaffMember | null; onClose(): void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const actions = useMemo(() => staffActions(api, shopId), [shopId]);
  const remove = useMutation({
    mutationFn: (m: StaffMember) => actions.remove(m.userId),
    onSuccess: (_r, m) => {
      qc.setQueryData<StaffMember[]>(staffKeys.team(shopId), (xs) => (xs ?? []).filter((x) => x.userId !== m.userId));
      void qc.invalidateQueries({ queryKey: staffKeys.team(shopId) });
      toast(`${displayName(m)} removed. They can’t open this shop any more.`);
      onClose();
    },
    onError: (e) => {
      toast(staffProblem(e as { status?: number; message?: string }, 'remove'), 'error');
      void qc.invalidateQueries({ queryKey: staffKeys.team(shopId) });
      onClose();
    },
  });
  const name = member ? displayName(member) : '';
  return (
    <ConfirmDialog
      open={!!member}
      title={`Remove ${name}?`}
      body={`${name} won’t be able to open this shop’s Partner Hub any more. You can add them again later.`}
      confirmLabel="Remove"
      busy={remove.isPending}
      onConfirm={() => member && remove.mutate(member)}
      onClose={onClose}
    />
  );
}
